import { PendingSchoolsBanner } from "./PendingSchoolsBanner";
import { DashboardTodo } from "./DashboardTodo";
import { pickPortfolio, rememberPortfolio } from "./selectedPortfolio";
import { DashSection, PortfolioHealth, SchoolScoresList, healthStats, useDashSections, pfScore, bandOf } from "./DashSections";
import { PartialTag } from "./PartialTag";
import { currentPeriodLabel } from "./portfolioFlow";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, Search, Building2, TrendingUp, AlertTriangle, CheckCircle2, GitCompare } from "lucide-react";
import { API, riskMeta, band, RISK_FILTER_OPTIONS, fmtScore, licenseLabel, fmtDate, PORTFOLIO_TYPE_LABELS } from "./shared";
import SandboxPanel from "@/components/SandboxPanel";
import IntelligencePanel from "@/components/IntelligencePanel";
import { RiskPill } from "./RiskPill";
import { BandTrendBadge } from "@/components/BandTrendBadge";
import { OverallRiskBanner, PillarHeatMap, DiagnosticHistory } from "./ExecutiveClarity";
import { PortfolioScoreMatrix } from "./PortfolioScoreMatrix";
import { FoundingPartnerAccess } from "@/components/FoundingPartnerAccess";
import { WelcomeSection } from "@/components/WelcomeSection";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { GapTrendBadge, useLargestGapTrend } from "./GapTrendBadge";

const RISK_ORDER = ["high_risk", "caution", "healthy", "no_data"];

const SummaryCard = ({ icon: Icon, label, value, sub, testid, tone = "default" }) => (
  <div
    data-testid={`portfolio-card-${testid}`}
    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-transform hover:-translate-y-0.5"
  >
    <div className="flex items-center gap-2 text-slate-500">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className={`mt-2 text-2xl font-semibold ${tone === "danger" ? "text-red-600" : "text-slate-900"}`}>{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
  </div>
);

export default function PortfolioDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [portfolios, setPortfolios] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [licenseFilter, setLicenseFilter] = useState("all");
  const [sortBy, setSortBy] = useState("risk");
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("portfolio_view_mode") || "matrix"; } catch { return "matrix"; }
  });
  const changeView = (m) => {
    setViewMode(m);
    try { localStorage.setItem("portfolio_view_mode", m); } catch { /* ignore */ }
  };
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [dashLabel, setDashLabel] = useState("Organization Dashboard");
  const [meName, setMeName] = useState("");
  const [periods, setPeriods] = useState([]);
  const [healthBand, setHealthBand] = useState(null);
  const sections = useDashSections(selectedId);
  const gapTrends = useLargestGapTrend(selectedId);
  useEffect(() => {
    if (!selectedId) return;
    fetch(`${API}/api/portfolio/${selectedId}/periods`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { periods: [] })).then((d) => setPeriods(d.periods || [])).catch(() => {});
  }, [selectedId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: me } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        if (alive) setMeName((me.name || me.full_name || me.email || "").split(/[ @]/)[0]);
        const headers = { "x-user-id": me.id || me.user_id, "x-user-role": me.role, "x-tenant-id": me.tenant_id || "tenant_1" };
        const { data } = await axios.get(`${API}/api/organizations/mine`, { headers, withCredentials: true });
        if (alive && data?.dashboard_label) setDashLabel(data.dashboard_label);
      } catch { /* keep default */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          setPortfolios(d.portfolios || []);
          if ((d.portfolios || []).length) {
            setSelectedId(pickPortfolio(d.portfolios).id);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setSelectedTenant(null);
    (async () => {
      setLoadingSchools(true);
      try {
        const r = await fetch(`${API}/api/portfolio/${selectedId}/schools-summary`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          setSchools(d.schools || []);
        }
      } finally {
        setLoadingSchools(false);
      }
    })();
  }, [selectedId, refreshKey]);

  const kpis = useMemo(() => {
    const scored = schools.filter((s) => s.score !== null && s.score !== undefined);
    const avg = scored.length ? Math.round(scored.reduce((a, s) => a + s.score, 0) / scored.length * 10) / 10 : null;
    const highRisk = schools.filter((s) => band(s.score) === "high_risk").length;
    const completed = schools.filter((s) => s.completion_pct >= 100).length;
    const evidenceGaps = schools.reduce((a, s) => a + (s.missing_evidence_count || 0), 0);
    const activeLicenses = schools.filter((s) => ["active", "manually_activated", "paid_active", "provisional_access"].includes(s.license_status)).length;
    return { count: schools.length, avg, highRisk, completed, evidenceGaps, activeLicenses };
  }, [schools]);

  const welcomeItems = useMemo(() => {
    const out = [];
    for (const s of schools) {
      const tid = s.tenant_id;
      const nm = s.school_name || "a school";
      const pa = s.portfolio_assessment || {};
      if (pa.status === "in_progress") {
        out.push({ signature: `pf:${selectedId}:pa_progress:${tid}`, priority: 4,
          lead: `Your assessment for ${nm} is in progress.`,
          actions: [{ label: "Continue Portfolio assessment", primary: true, onClick: () => navigate("/portfolio/periods") }] });
      } else if (s.assessment_is_final) {
        out.push({ signature: `pf:${selectedId}:review:${tid}`, priority: 5,
          lead: `${nm}'s diagnostic is final.`,
          actions: [{ label: "Review school results", primary: true, onClick: () => navigate(`/portfolio/schools/${tid}`) }] });
      }
    }
    out.sort((a, b) => a.priority - b.priority);
    return out.slice(0, 8);
  }, [schools, selectedId, navigate]);

  const visible = useMemo(() => {
    let rows = [...schools];
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((s) => (s.school_name || "").toLowerCase().includes(q));
    }
    if (riskFilter !== "all") rows = rows.filter((s) => bandOf(pfScore(s)) === riskFilter);
    if (licenseFilter !== "all") {
      rows = licenseFilter === "active"
        ? rows.filter((s) => ["active", "manually_activated", "paid_active", "provisional_access"].includes(s.license_status))
        : rows.filter((s) => !["active", "manually_activated", "paid_active", "provisional_access"].includes(s.license_status));
    }
    rows.sort((a, b) => {
      if (sortBy === "name") return (a.school_name || "").localeCompare(b.school_name || "");
      if (sortBy === "score") return (b.score ?? -1) - (a.score ?? -1);
      if (sortBy === "completion") return (b.completion_pct ?? 0) - (a.completion_pct ?? 0);
      // risk: worst first
      return RISK_ORDER.indexOf(a.risk_level) - RISK_ORDER.indexOf(b.risk_level);
    });
    return rows;
  }, [schools, search, riskFilter, licenseFilter, sortBy]);

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  if (!portfolios.length) {
    return (
      <div data-testid="portfolio-empty-state" className="mx-auto mt-16 max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <Building2 className="mx-auto h-10 w-10 text-slate-300" />
        <h2 className="mt-4 text-lg font-semibold text-slate-900">No Schools Assigned</h2>
        <p className="mt-2 text-sm text-slate-500">
          You don't have any schools assigned to your portfolio yet. Please contact your 589 Labs administrator to be assigned.
        </p>
      </div>
    );
  }

  const selected = portfolios.find((p) => p.id === selectedId);
  const hs = healthStats(schools);
  const allOpen = sections.open.length === 4;
  const latestPeriodName = (periods || []).find((p) => p.enable_portfolio_assessments)?.name;
  const pickBand = (k) => {
    const next = healthBand === k ? null : k;
    setHealthBand(next);
    if (next) {
      sections.toggle("scores", true);
      setTimeout(() => document.getElementById("dash-scores")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  };
  const selectedSchool = schools.find((s) => s.tenant_id === selectedTenant) || null;

  return (
    <div data-testid="portfolio-dashboard" className="space-y-6">
      <WelcomeSection name={meName} items={welcomeItems} testid="portfolio-welcome" whatsNew />
      {!loadingSchools && schools.length === 0 && <PendingSchoolsBanner />}
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="portfolio-dashboard-title">Dashboard</h1>
          {selected && (
            <p className="mt-1 text-sm text-slate-500">
              {selected.name} · {PORTFOLIO_TYPE_LABELS[selected.type] || selected.type}
            </p>
          )}
          <div className="mt-2"><FoundingPartnerAccess withCredentials /></div>
        </div>
        {portfolios.length > 1 && (
          <Select value={selectedId} onValueChange={(v) => { setSelectedId(v); rememberPortfolio(v); const u = new URL(window.location.href); if (u.searchParams.has("pid")) { u.searchParams.delete("pid"); window.history.replaceState(null, "", u.pathname + u.search); } }}>
            <SelectTrigger data-testid="portfolio-switcher" className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {portfolios.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary cards */}
      <SandboxPanel
        authFetch={(url, opts = {}) => fetch(url, { ...opts, credentials: "include" })}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
      <IntelligencePanel
        storageKey="portfolio"
        authFetch={(url, opts = {}) => fetch(url, { ...opts, credentials: "include" })}
        getContext={() => ({ page: "portfolio_dashboard", portfolio: selected?.name,
          school_count: schools.length, date: new Date().toISOString().slice(0, 10) })}
      />
      <PortfolioHealth stats={hs} periodName={latestPeriodName} active={healthBand} onPick={pickBand} />
      <DashboardTodo pid={selectedId} />
      <div className="hidden justify-end sm:flex">
        <button onClick={() => sections.setAll(allOpen ? [] : ["scores", "matrix", "benchmark", "heatmap"])} data-testid="dash-expand-all"
          className="text-[13px] font-semibold text-[#005f7b] underline">{allOpen ? "Collapse all" : "Expand all"}</button>
      </div>
      <DashSection id="scores" title="School scores" open={sections.open.includes("scores")} onToggle={() => sections.toggle("scores")}
        summary={<span data-testid="portfolio-summary-cards"><b data-testid="portfolio-card-assigned-schools">{hs.total}</b> schools · {hs.high_risk} High Risk · {hs.caution} Caution{hs.awaiting ? ` · ${hs.awaiting} awaiting` : ""} · Avg <b data-testid="portfolio-card-avg-score">{hs.avg === null ? "—" : hs.avg.toFixed(1)}</b> (your assessments) · <b data-testid="portfolio-card-completed">{hs.final}</b> of {hs.total} final</span>}>
        {healthBand && (
          <p className="mb-2 text-xs text-slate-600" data-testid="school-scores-filter">Showing {healthBand === "high_risk" ? "High Risk" : healthBand === "caution" ? "Caution" : "Healthy"} schools · <button className="font-semibold text-[#005f7b] underline" onClick={() => setHealthBand(null)}>Show all</button></p>
        )}
        <SchoolScoresList schools={schools} band={healthBand} periods={periods} pid={selectedId} />
      </DashSection>
      <DashSection id="matrix" title="Score Matrix" open={sections.open.includes("matrix")} onToggle={() => sections.toggle("matrix")}
        summary={`${schools.length} schools · 8 pillars`}>
        <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            data-testid="portfolio-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search schools…"
            className="h-9 w-56 pl-8"
          />
        </div>
        <Select value={riskFilter} onValueChange={setRiskFilter}>
          <SelectTrigger data-testid="portfolio-filter-risk" className="h-9 w-44"><SelectValue placeholder="Risk" /></SelectTrigger>
          <SelectContent>
            {RISK_FILTER_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={licenseFilter} onValueChange={setLicenseFilter}>
          <SelectTrigger data-testid="portfolio-filter-license" className="h-9 w-40"><SelectValue placeholder="License" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Licenses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="portfolio-sort" className="h-9 w-40"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="risk">Sort: Risk</SelectItem>
            <SelectItem value="score">Sort: Score</SelectItem>
            <SelectItem value="completion">Sort: Completion</SelectItem>
            <SelectItem value="name">Sort: Name</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="portfolio-view-toggle">
          <button onClick={() => changeView("list")} className={`px-3 h-9 text-xs font-semibold ${viewMode === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="view-list-btn">List</button>
          <button onClick={() => changeView("matrix")} className={`px-3 h-9 text-xs font-semibold ${viewMode === "matrix" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="view-matrix-btn">Score Matrix</button>
        </div>
      </div>

      {viewMode === "matrix" ? (
        loadingSchools ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <PortfolioScoreMatrix schools={visible} onSchool={(s) => navigate(`/portfolio/schools/${s.tenant_id}`)} onCell={(s) => navigate(`/portfolio/schools/${s.tenant_id}`)} />
        )
      ) : (
        loadingSchools ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {/* Desktop table */}
          <div data-testid="portfolio-school-list" className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">School</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Risk</th>
                  <th className="px-4 py-3">Momentum</th>
                  <th className="px-4 py-3">Completion</th>
                  <th className="px-4 py-3">Evidence</th>
                  <th className="px-4 py-3">License</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((s) => (
                  <tr key={s.tenant_id} data-testid={`portfolio-school-row-${s.tenant_id}`} onClick={() => { setSelectedTenant(s.tenant_id); sections.toggle("heatmap", true); }} className={`cursor-pointer ${selectedTenant === s.tenant_id ? "bg-red-50/60" : "hover:bg-slate-50"}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{s.school_name}</div>
                      <div className="text-xs text-slate-400">{currentPeriodLabel(periods, s.tenant_id) || s.latest_period || "No diagnostic"}</div>
                      <GapTrendBadge trend={gapTrends[s.tenant_id]} testid={`gap-trend-${s.tenant_id}`} />
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{fmtScore(s.score)} <PartialTag answered={s.answered} total={s.total_indicators} final={!!s.assessment_is_final} score={s.score} testid={`partial-${s.tenant_id}`} /></td>
                    <td className="px-4 py-3"><RiskPill level={s.risk_level} score={s.score} /></td>
                    <td className="px-4 py-3">
                      {(() => {
                        const bt = <BandTrendBadge score={s.score} prevScore={s.prev_score} showThisPeriod={false} testid={`portfolio-trend-${s.tenant_id}`} />;
                        return bt || <span className="text-xs text-slate-300">—</span>;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{s.completion_pct}%</td>
                    <td className="px-4 py-3 text-slate-600">{s.missing_evidence_count} missing</td>
                    <td className="px-4 py-3 text-slate-600">{licenseLabel(s.license_status)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`portfolio-view-school-btn-${s.tenant_id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/portfolio/schools/${s.tenant_id}`); }}
                        className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        View School
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!visible.length && <div className="p-8 text-center text-sm text-slate-400">No schools match your filters.</div>}
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {visible.map((s) => (
              <div key={s.tenant_id} data-testid={`portfolio-school-card-${s.tenant_id}`} onClick={() => { setSelectedTenant(s.tenant_id); sections.toggle("heatmap", true); }} className={`rounded-xl border bg-white p-4 shadow-sm ${selectedTenant === s.tenant_id ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-slate-900">{s.school_name}</div>
                    <div className="text-xs text-slate-400">{currentPeriodLabel(periods, s.tenant_id) || s.latest_period || "No diagnostic"}</div>
                    <GapTrendBadge trend={gapTrends[s.tenant_id]} testid={`gap-trend-card-${s.tenant_id}`} />
                  </div>
                  <div className="text-lg font-semibold text-slate-800">{fmtScore(s.score)}</div>
                  <PartialTag answered={s.answered} total={s.total_indicators} final={!!s.assessment_is_final} score={s.score} testid={`partial-card-${s.tenant_id}`} />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <RiskPill level={s.risk_level} score={s.score} />
                  <span className="text-xs text-slate-500">{s.completion_pct}% · {s.missing_evidence_count} ev. missing</span>
                </div>
                <div className="mt-2"><BandTrendBadge score={s.score} prevScore={s.prev_score} testid={`portfolio-trend-card-${s.tenant_id}`} /></div>
                <button
                  data-testid={`portfolio-view-school-btn-${s.tenant_id}`}
                  onClick={(e) => { e.stopPropagation(); navigate(`/portfolio/schools/${s.tenant_id}`); }}
                  className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  View School
                </button>
              </div>
            ))}
            {!visible.length && <div className="p-6 text-center text-sm text-slate-400">No schools match your filters.</div>}
          </div>
        </>
      ))}

        </div>
      </DashSection>
      <DashSection id="benchmark" title="Benchmark Improvement Analysis" open={sections.open.includes("benchmark")} onToggle={() => sections.toggle("benchmark")}
        summary="Compare up to 3 schools against a benchmark">
      {/* Benchmark Improvement Analysis CTA */}
      <div data-testid="benchmark-cta-card" className="rounded-xl border border-[#e5e7eb] bg-gradient-to-r from-white to-[#fef2f2] p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#fee2e2] flex items-center justify-center shrink-0"><GitCompare className="h-5 w-5 text-[#b91c1c]" /></div>
          <div>
            <h3 className="text-base font-bold text-[#111827]">Benchmark Improvement Analysis</h3>
            <p className="text-[13px] text-[#6b7280] mt-0.5 max-w-xl">Compare up to three schools, select a benchmark school, and identify management-focused actions that can help lower-scoring schools improve.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="benchmark-start-btn" onClick={() => navigate(`/portfolio/benchmark${selectedId ? `?pid=${selectedId}` : ""}`)} className="rounded-md bg-[#b91c1c] px-4 py-2 text-sm font-semibold text-white hover:bg-[#991b1b]">Start Benchmark Analysis</button>
        </div>
      </div>

      </DashSection>
      <DashSection id="heatmap" title="Pillar Health Heat Map" open={sections.open.includes("heatmap")} onToggle={() => sections.toggle("heatmap")}
        summary={selectedSchool ? `Showing ${selectedSchool.school_name}` : "Pick a school to view"}>
      {/* Pillar Health Heat Map for the selected school */}
      <div data-testid="portfolio-pillar-section" className="space-y-8 pt-2">
        {!selectedSchool ? (
          <div data-testid="portfolio-pillar-empty" className="rounded-xl border border-[#e5e7eb] bg-white p-8 text-center text-sm text-[#6b7280] shadow-sm">
            Select a school from the list above to view its Pillar Health Heat Map.
          </div>
        ) : !selectedSchool.has_diagnostic ? (
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-8 text-center text-sm text-[#6b7280] shadow-sm">
            No completed Diagnostic available for {selectedSchool.school_name}.
          </div>
        ) : (
          <>
            <div className="my-2">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-[#6b7280]">Selected School</h2>
                <span data-testid="portfolio-pillar-school-name" className="text-sm font-bold text-[#111827]">{selectedSchool.school_name}</span>
              </div>
              <OverallRiskBanner score={selectedSchool.score} riskBand={selectedSchool.overall_risk_band} provisional={!selectedSchool.assessment_is_final} />
            </div>
            <PillarHeatMap pillars={selectedSchool.pillars} onReview={(p) => navigate(`/portfolio/schools/${selectedSchool.tenant_id}/diagnostic?pillar=${p.id}${selectedSchool.diagnostic_id ? `&diag=${selectedSchool.diagnostic_id}` : ""}`)} />
            <DiagnosticHistory history={selectedSchool.history} tenantId={selectedSchool.tenant_id} />
          </>
        )}
      </div>
      </DashSection>
    </div>
  );
}
